/**
 * @file Token generation and validation for magic links
 * Provides secure token management for quote access and validation
 */

import { supabase } from "@/integrations/supabase/client";
import { decode as decodeBase64, encodeBase64 } from "@/lib/utils";
import { debug } from "@/lib/utils";
import { config } from "@/lib/config";
import { QuoteTokenParams } from "@/lib/types/quote-types";

/**
 * Interface for quote token payload - contains all data required for token validation
 */
export interface QuoteToken {
  /** The ID of the quote */
  quoteId: string;   
  /** The ID of the tradesperson */
  tradeId: string;   
  /** Expiration timestamp (Unix timestamp) */
  exp: number;       
  /** Unique token ID (UUID) */
  jti: string;       
}

/**
 * Token validation result with extended information
 */
export interface TokenValidationResult {
  /** Whether the token is valid */
  isValid: boolean;
  /** The decoded token if valid, null otherwise */
  token: QuoteToken | null;
  /** Reason for validation failure, if any */
  reason?: 'expired' | 'not_found' | 'invalid_format' | 'unknown';
  /** Time remaining before expiration (in seconds) if valid */
  expiresIn?: number;
}

/**
 * Generate a secure token for quote access
 * Creates a token with expiration and stores it in the database
 * 
 * @param params Token generation parameters (quoteId, tradeId, expiration)
 * @returns The generated token string
 * @throws Error if token generation fails
 */
export async function generateQuoteToken(
  quoteId: string, 
  tradeId: string,
  expiresIn?: number
): Promise<string>;

export async function generateQuoteToken(
  params: QuoteTokenParams
): Promise<string>;

export async function generateQuoteToken(
  quoteIdOrParams: string | QuoteTokenParams,
  tradeId?: string,
  expiresIn?: number
): Promise<string> {
  try {
    // Handle overloaded parameters
    let quoteId: string;
    let tradeIdValue: string;
    let expirationSeconds: number;

    if (typeof quoteIdOrParams === 'string') {
      quoteId = quoteIdOrParams;
      tradeIdValue = tradeId!;
      expirationSeconds = expiresIn || config.tokenExpirationTime;
    } else {
      quoteId = quoteIdOrParams.quoteId;
      tradeIdValue = quoteIdOrParams.tradeId;
      expirationSeconds = quoteIdOrParams.expiresIn || config.tokenExpirationTime;
    }
    
    // Generate a unique ID for this token
    const jti = crypto.randomUUID();
    
    // Set expiration time
    const expirationTime = Math.floor(Date.now() / 1000) + expirationSeconds;
    
    // Create the token payload
    const payload: QuoteToken = {
      quoteId,
      tradeId: tradeIdValue,
      exp: expirationTime,
      jti
    };
    
    // Encode payload to base64
    const tokenPayload = encodeBase64(JSON.stringify(payload));
    
    // Store token in database for validation
    const { error } = await supabase
      .from('quote_tokens')
      .insert({
        jti,
        quote_id: quoteId,
        trade_id: tradeIdValue,
        expires_at: new Date(expirationTime * 1000).toISOString(),
        created_at: new Date().toISOString() // Explicitly set creation time
      });
    
    if (error) {
      throw error;
    }
    
    return tokenPayload;
  } catch (error) {
    debug.error('Error generating quote token:', error);
    throw new Error('Failed to generate secure access token');
  }
}

/**
 * Validate a quote token
 * Checks token validity, expiration, and existence in the database
 * 
 * @param token The token string to validate
 * @returns Detailed validation result with token data if valid
 */
export async function validateQuoteToken(token: string): Promise<TokenValidationResult> {
  try {
    // Decode the token
    const decodedPayload = JSON.parse(decodeBase64(token)) as QuoteToken;
    
    // Check token format
    if (!decodedPayload || !decodedPayload.quoteId || !decodedPayload.tradeId || !decodedPayload.jti) {
      return {
        isValid: false,
        token: null,
        reason: 'invalid_format'
      };
    }
    
    // Check if token has expired
    const currentTime = Math.floor(Date.now() / 1000);
    if (decodedPayload.exp < currentTime) {
      return {
        isValid: false,
        token: null,
        reason: 'expired'
      };
    }
    
    // Verify the token exists in the database
    const { data, error } = await supabase
      .from('quote_tokens')
      .select('*')
      .eq('jti', decodedPayload.jti)
      .eq('quote_id', decodedPayload.quoteId)
      .eq('trade_id', decodedPayload.tradeId)
      .single();
    
    if (error || !data) {
      debug.warn('Token not found in database');
      return {
        isValid: false,
        token: null,
        reason: 'not_found'
      };
    }
    
    // Update last accessed timestamp for the quote
    await supabase
      .from('quotes')
      .update({ last_accessed_at: new Date().toISOString() })
      .eq('id', decodedPayload.quoteId);
    
    return {
      isValid: true,
      token: decodedPayload,
      expiresIn: decodedPayload.exp - currentTime
    };
  } catch (error) {
    debug.error('Error validating quote token:', error);
    return {
      isValid: false,
      token: null,
      reason: 'unknown'
    };
  }
}

/**
 * Revoke a quote token to invalidate it
 * 
 * @param jti The unique token identifier
 * @returns boolean indicating success
 */
export async function revokeQuoteToken(jti: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('quote_tokens')
      .delete()
      .eq('jti', jti);
    
    return !error;
  } catch (error) {
    debug.error('Error revoking quote token:', error);
    return false;
  }
}

/**
 * Construct a magic link URL for a token
 * 
 * @param token The token to include in the URL
 * @returns The full magic link URL
 */
export function constructMagicLink(token: string): string {
  return `${config.magicLinkBaseUrl}/trades/quote/${token}`;
}

/**
 * Clean up expired tokens from the database
 * 
 * @returns The number of tokens deleted
 */
export async function cleanupExpiredTokens(): Promise<number> {
  try {
    const now = new Date().toISOString();
    
    // Query to find and delete expired tokens
    const { data, error } = await supabase
      .from('quote_tokens')
      .delete()
      .lt('expires_at', now)
      .select('count');
    
    if (error) {
      debug.error('Error cleaning up expired tokens:', error);
      return 0;
    }
    
    const deletedCount = data?.length || 0;
    debug.log(`Cleaned up ${deletedCount} expired tokens`);
    return deletedCount;
  } catch (error) {
    debug.error('Error in token cleanup process:', error);
    return 0;
  }
}